import {AlertTriangle, Home, RefreshCw} from 'lucide-react';
import {Component, ErrorInfo, ReactNode} from 'react';
import {useNavigate, useRouteError} from 'react-router';

type ErrorBoundaryState = {
    hasError: boolean;
    error: Error | null;
};

type ErrorBoundaryProps = {
    children: ReactNode;
    fallback?: ReactNode;
};

/**
 * Class-based Error Boundary for catching render errors
 * Used as a wrapper around route content
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {hasError: false, error: null};
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReset = (): void => {
        this.setState({hasError: false, error: null});
    };

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
        }

        return this.props.children;
    }
}

type ErrorFallbackProps = {
    error: Error | null;
    onReset?: () => void;
};

/**
 * Error fallback UI component
 * Displays a user-friendly error message with recovery options
 */
export const ErrorFallback = ({error, onReset}: ErrorFallbackProps) => {
    const handleRefresh = () => {
        if (onReset) {
            onReset();
        }
        window.location.reload();
    };

    const handleGoHome = () => {
        window.location.href = '/';
    };

    return (
        <div
            className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-6"
            role="alert"
            aria-live="assertive"
        >
            <div className="max-w-md w-full text-center">
                <div
                    className="w-20 h-20 rounded-full bg-[var(--error-soft)] flex items-center justify-center mx-auto mb-6"
                    aria-hidden="true"
                >
                    <AlertTriangle size={40} className="text-[var(--error)]" />
                </div>

                <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
                    Ops! Algo deu errado
                </h1>

                <p className="text-[var(--text-secondary)] mb-6">
                    Ocorreu um erro inesperado. Por favor, tente novamente ou volte para a página
                    inicial.
                </p>

                {/* v8 ignore start */}
                {error && import.meta.env.DEV && (
                    <div className="bg-[var(--bg-tertiary)] rounded-xl p-4 mb-6 text-left">
                        <p className="text-xs font-mono text-[var(--text-muted)] break-all">
                            {error.message}
                        </p>
                    </div>
                )}
                {/* v8 ignore stop */}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                        onClick={handleRefresh}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-[var(--accent)] text-white rounded-xl font-semibold active:scale-[0.98] transition-transform"
                        aria-label="Recarregar página"
                    >
                        <RefreshCw size={18} aria-hidden="true" />
                        Tentar novamente
                    </button>

                    <button
                        onClick={handleGoHome}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-xl font-semibold active:scale-[0.98] transition-transform border border-[var(--border-color)]"
                        aria-label="Voltar para página inicial"
                    >
                        <Home size={18} aria-hidden="true" />
                        Página inicial
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Route Error Element for React Router
 * Uses useRouteError to get the error thrown by loaders/actions
 */
export const RouteErrorBoundary = () => {
    const error = useRouteError() as Error;

    return <ErrorFallback error={error} />;
};

/**
 * Hook-based error boundary wrapper for functional components
 * Use with React Router's errorElement prop
 */
export const RouteErrorElement = () => {
    const navigate = useNavigate();
    const error = useRouteError() as Error;

    const handleGoHome = () => {
        navigate('/');
    };

    return (
        <div
            className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-6"
            role="alert"
            aria-live="assertive"
        >
            <div className="max-w-md w-full text-center">
                <div
                    className="w-20 h-20 rounded-full bg-[var(--error-soft)] flex items-center justify-center mx-auto mb-6"
                    aria-hidden="true"
                >
                    <AlertTriangle size={40} className="text-[var(--error)]" />
                </div>

                <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
                    Página não encontrada
                </h1>

                <p className="text-[var(--text-secondary)] mb-6">
                    A página que você está procurando não existe ou ocorreu um erro.
                </p>

                {/* v8 ignore start */}
                {error && import.meta.env.DEV && (
                    <div className="bg-[var(--bg-tertiary)] rounded-xl p-4 mb-6 text-left">
                        <p className="text-xs font-mono text-[var(--text-muted)] break-all">
                            {error.message || String(error)}
                        </p>
                    </div>
                )}
                {/* v8 ignore stop */}

                <button
                    onClick={handleGoHome}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-[var(--accent)] text-white rounded-xl font-semibold active:scale-[0.98] transition-transform mx-auto"
                    aria-label="Voltar para página inicial"
                >
                    <Home size={18} aria-hidden="true" />
                    Voltar ao início
                </button>
            </div>
        </div>
    );
};
